import { useEffect, useMemo, useRef, useState } from 'react';
import { admin } from '@repletoryreach/sdk-api';
import { markdownToBlocks, type ArticleBlock } from '@repletoryreach/sdk-utils/markdown';
import { Stack, Group, Button, Text, Badge } from '@repletoryreach/sdk-ui';
import { TextInput, Textarea, Select, Switch } from '@repletoryreach/sdk-ui/inputs';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { SegmentedControl } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useCreateBlogPost, useUpdateBlogPost, useUploadBlogAsset } from '../../../../data/useBlog';
import { useT } from '../../../../i18n/I18nProvider';
import '../blog.css';

/** The categories the news page dresses differently; any other text is allowed too. */
const CATEGORIES = ['Product update', 'Pricing', 'Announcement', 'Guide', 'Company'];

/** Where the site serves an uploaded file from, so the preview shows the real picture. */
const assetUrl = (id: string) => `${((import.meta.env.VITE_API_BASE as string | undefined) ?? '/api').replace(/\/+$/, '')}/v1/public/blog/assets/${id}`;

/** An ISO instant as the value a `datetime-local` field takes, in the browser's own time. */
function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Write or edit a story. Markdown on the left, the article as the site will
 * show it on the right. Pictures and video upload from here and drop into the
 * text as `![caption](asset:ID)` and `!video[caption](asset:ID)`; a YouTube
 * or Vimeo link on its own line becomes a player.
 */
export function PostEditorDrawer({ post, opened, onClose }: { post: admin.BlogPost | 'new' | null; opened: boolean; onClose: () => void }) {
  const t = useT();
  const create = useCreateBlogPost();
  const update = useUpdateBlogPost();
  const upload = useUploadBlogAsset();
  const editing = post && post !== 'new' ? post : null;

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [excerpt, setExcerpt] = useState('');
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [customCategory, setCustomCategory] = useState('');
  const [author, setAuthor] = useState('RepletoryReach');
  const [cover, setCover] = useState('');
  const [body, setBody] = useState('');
  const [publishNow, setPublishNow] = useState(false);
  const [publishedAt, setPublishedAt] = useState('');
  const [pane, setPane] = useState<'write' | 'preview'>('write');
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const coverRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!opened) return;
    setPane('write');
    setSlugTouched(Boolean(editing));
    setTitle(editing?.title ?? '');
    setSlug(editing?.slug ?? '');
    setExcerpt(editing?.excerpt ?? '');
    const cat = editing?.category ?? CATEGORIES[0];
    setCategory(CATEGORIES.includes(cat) ? cat : 'other');
    setCustomCategory(CATEGORIES.includes(cat) ? '' : cat);
    setAuthor(editing?.author ?? 'RepletoryReach');
    setCover(editing?.cover ?? '');
    setBody(editing?.body_markdown ?? '');
    setPublishNow(editing?.status === 'published');
    setPublishedAt(toLocalInput(editing?.published_at));
  }, [opened, editing]);

  // The address follows the title until the author touches it.
  useEffect(() => {
    if (slugTouched) return;
    setSlug(title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''));
  }, [title, slugTouched]);

  const blocks: ArticleBlock[] = useMemo(() => markdownToBlocks(body, { assetUrl }), [body]);
  const words = body.split(/\s+/).filter(Boolean).length;
  const finalCategory = category === 'other' ? customCategory.trim() || 'Notes' : category;
  // The field holds local time; the API takes an instant. Blank leaves the date to the moment of publishing.
  const publishedInstant = publishedAt ? new Date(publishedAt) : null;
  const publishedBad = publishedInstant !== null && Number.isNaN(publishedInstant.getTime());

  function insertAtCursor(snippet: string) {
    const el = bodyRef.current;
    const start = el?.selectionStart ?? body.length;
    const end = el?.selectionEnd ?? body.length;
    const before = body.slice(0, start);
    const after = body.slice(end);
    const pad = before.length && !before.endsWith('\n') ? '\n\n' : '';
    const next = `${before}${pad}${snippet}\n\n${after}`;
    setBody(next);
    requestAnimationFrame(() => { el?.focus(); const pos = (before + pad + snippet).length; el?.setSelectionRange(pos, pos); });
  }

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>, into: 'body' | 'cover') {
    const file = e.currentTarget.files?.[0];
    e.currentTarget.value = '';
    if (!file) return;
    try {
      const res = await upload.mutateAsync(file);
      const ref = `asset:${res.data.id}`;
      if (into === 'cover') { setCover(ref); notifySuccess(t('content.blog.editor.coverSet'), t('content.blog.title')); return; }
      const video = res.data.content_type.startsWith('video/');
      insertAtCursor(video ? `!video[${file.name.replace(/\.[^.]+$/, '')}](${ref})` : `![${file.name.replace(/\.[^.]+$/, '')}](${ref})`);
      notifySuccess(t('content.blog.editor.inserted', { name: file.name }), t('content.blog.title'));
    } catch (err) {
      notifyError(err instanceof Error ? err.message : t('common.unknownError'), t('content.blog.editor.uploadFailed'));
    }
  }

  function insertVideoLink() {
    const url = window.prompt(t('content.blog.editor.videoPrompt'));
    if (!url) return;
    insertAtCursor(`!video[](${url.trim()})`);
  }

  async function save() {
    const req: admin.UpsertBlogPostRequest = {
      title: title.trim(),
      slug: slug.trim(),
      excerpt: excerpt.trim(),
      body_markdown: body,
      category: finalCategory,
      author: author.trim(),
      cover: cover.trim(),
      status: publishNow ? 'published' : 'draft',
      ...(publishedInstant && !publishedBad ? { published_at: publishedInstant.toISOString() } : {})
    };
    try {
      const res = editing ? await update.mutateAsync({ id: editing.id, ...req }) : await create.mutateAsync(req);
      notifySuccess(res.data.status === 'published' ? t('content.blog.editor.savedLive', { title: res.data.title }) : t('content.blog.editor.savedDraft', { title: res.data.title }), t('content.blog.title'));
      onClose();
    } catch (err) {
      notifyError(err instanceof Error ? err.message : t('common.unknownError'), t('content.blog.editor.saveFailed'));
    }
  }

  const busy = create.isPending || update.isPending;

  return (
    <Drawer opened={opened} onClose={onClose} title={editing ? t('content.blog.editor.editTitle') : t('content.blog.editor.newTitle')} position="right" size="xl">
      <Stack gap="md">
        <TextInput label={t('content.blog.editor.headline')} value={title} onChange={(e) => setTitle(e.currentTarget.value)} required placeholder={t('content.blog.editor.headlinePlaceholder')} />
        <Group gap="sm" grow align="flex-start">
          <TextInput
            label={t('content.blog.editor.slug')}
            value={slug}
            onChange={(e) => { setSlugTouched(true); setSlug(e.currentTarget.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')); }}
            description={t('content.blog.editor.slugHint', { url: `/blog/${slug || 'your-story'}` })}
          />
          <Select
            label={t('content.blog.editor.category')}
            value={category}
            onChange={(e) => setCategory(e.currentTarget.value)}
            data={[...CATEGORIES.map((c) => ({ value: c, label: c })), { value: 'other', label: t('content.blog.editor.categoryOther') }]}
            description={t('content.blog.editor.categoryHint')}
          />
        </Group>
        {category === 'other' && (
          <TextInput label={t('content.blog.editor.categoryCustom')} value={customCategory} onChange={(e) => setCustomCategory(e.currentTarget.value)} />
        )}
        <Textarea label={t('content.blog.editor.excerpt')} value={excerpt} onChange={(e) => setExcerpt(e.currentTarget.value)} autosize minRows={2} maxRows={4} description={t('content.blog.editor.excerptHint')} />
        <Group gap="sm" grow align="flex-start">
          <TextInput label={t('content.blog.editor.author')} value={author} onChange={(e) => setAuthor(e.currentTarget.value)} />
          <TextInput
            label={t('content.blog.editor.cover')}
            value={cover}
            onChange={(e) => setCover(e.currentTarget.value)}
            placeholder="asset:… or https://…"
            description={t('content.blog.editor.coverHint')}
            rightSection={<Button size="xs" variant="subtle" loading={upload.isPending} onClick={() => coverRef.current?.click()}>{t('content.blog.editor.upload')}</Button>}
            rightSectionWidth={80}
          />
        </Group>
        <input ref={coverRef} type="file" accept="image/*" hidden onChange={(e) => void onPickFile(e, 'cover')} />

        <Stack gap="xs">
          <Group justify="space-between" align="center" wrap="wrap">
            <SegmentedControl size="xs" value={pane} onChange={(v) => setPane(v as 'write' | 'preview')} data={[{ value: 'write', label: t('content.blog.editor.write') }, { value: 'preview', label: t('content.blog.editor.preview') }]} />
            <Group gap="xs">
              <Button size="xs" variant="subtle" onClick={() => insertAtCursor('## ')}>{t('content.blog.editor.heading')}</Button>
              <Button size="xs" variant="subtle" onClick={() => insertAtCursor('- ')}>{t('content.blog.editor.list')}</Button>
              <Button size="xs" variant="subtle" onClick={() => insertAtCursor('> ')}>{t('content.blog.editor.quote')}</Button>
              <Button size="xs" variant="subtle" loading={upload.isPending} onClick={() => fileRef.current?.click()}>{t('content.blog.editor.picture')}</Button>
              <Button size="xs" variant="subtle" onClick={insertVideoLink}>{t('content.blog.editor.video')}</Button>
            </Group>
          </Group>
          <input ref={fileRef} type="file" accept="image/*,video/mp4,video/webm" hidden onChange={(e) => void onPickFile(e, 'body')} />
          {pane === 'write' ? (
            <Textarea
              ref={bodyRef}
              aria-label={t('content.blog.editor.body')}
              value={body}
              onChange={(e) => setBody(e.currentTarget.value)}
              minRows={16}
              maxRows={40}
              autosize
              className="rr-blog-editor__body"
              placeholder={t('content.blog.editor.bodyPlaceholder')}
              description={t('content.blog.editor.bodyHint')}
            />
          ) : (
            <div className="rr-blog-preview">
              <h1>{title || t('content.blog.editor.headlinePlaceholder')}</h1>
              {excerpt && <p className="rr-blog-preview__lede">{excerpt}</p>}
              {blocks.length === 0 && <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('content.blog.editor.previewEmpty')}</Text>}
              {blocks.map((b, i) => <PreviewBlock key={i} block={b} />)}
            </div>
          )}
          <Group gap="sm" align="center">
            <Text role="caption" c="var(--rr-color-text-muted)">{t('content.blog.editor.words', { count: words, minutes: Math.max(1, Math.ceil(words / 200)) })}</Text>
            {editing && <Badge size="xs" variant="light" color={editing.status === 'published' ? 'green' : 'gray'}>{editing.status === 'published' ? t('content.blog.status.published') : t('content.blog.status.draft')}</Badge>}
          </Group>
        </Stack>

        <Switch checked={publishNow} onChange={setPublishNow} label={t('content.blog.editor.publish')} description={t('content.blog.editor.publishHint')} />
        <TextInput
          type="datetime-local"
          label={t('content.blog.editor.publishedAt')}
          value={publishedAt}
          onChange={(e) => setPublishedAt(e.currentTarget.value)}
          description={t('content.blog.editor.publishedAtHint')}
          error={publishedBad ? t('content.blog.editor.publishedAtBad') : undefined}
          data-testid="blog-published-at"
        />

        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={onClose}>{t('action.cancel')}</Button>
          <Button loading={busy} disabled={!title.trim() || publishedBad} onClick={() => void save()}>{publishNow ? t('content.blog.editor.savePublish') : t('content.blog.editor.saveDraft')}</Button>
        </Group>
      </Stack>
    </Drawer>
  );
}

/** The article as the site lays it out, enough to judge the words and the pictures. */
function PreviewBlock({ block }: { block: ArticleBlock }) {
  switch (block.type) {
    case 'heading': return block.level === 3 ? <h3>{block.text}</h3> : <h2>{block.text}</h2>;
    case 'paragraph': return <p>{block.text}</p>;
    case 'quote': return <blockquote>{block.text}</blockquote>;
    case 'list': return block.ordered ? <ol>{block.items.map((i, k) => <li key={k}>{i}</li>)}</ol> : <ul>{block.items.map((i, k) => <li key={k}>{i}</li>)}</ul>;
    case 'code': return <pre><code>{block.text}</code></pre>;
    case 'image': return <figure><img src={block.src} alt={block.alt} />{block.caption && <figcaption>{block.caption}</figcaption>}</figure>;
    case 'video': return <figure>{block.provider === 'file' ? <video src={block.src} controls /> : <iframe src={block.src} title={block.caption ?? 'Video'} allowFullScreen />}{block.caption && <figcaption>{block.caption}</figcaption>}</figure>;
    default: return null;
  }
}
